// Daily Summary Modal
// Dismissible end-of-day report card. Triggered by a DAILY_SUMMARY WS message
// (or at market close). Renders the same data as the engine's boxed terminal report
// but as a structured, readable UI.

import React from 'react';
import { X, TrendingUp, TrendingDown, Award, BarChart2, Clock } from 'lucide-react';
import { useEngine } from '../context/MarketContext';

function formatTZS(v: number): string {
  const abs = Math.abs(v);
  const sign = v >= 0 ? '+' : '−';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${abs.toFixed(0)}`;
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 10, color: '#7a8693', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: color || '#d7dde4' }}>{value}</span>
    </div>
  );
}

export default function DailySummaryModal() {
  const { dailySummary, dismissDailySummary } = useEngine();
  if (!dailySummary) return null;

  const d = dailySummary;
  const totalPnL = d.realized_pnl + d.unrealized_pnl;
  const pnlColor = totalPnL >= 0 ? '#36c98e' : '#ff5c5c';
  const dayProgress = d.total_days != null ? `Day ${d.day_number} / ${d.total_days}` : `Day ${d.day_number}`;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={dismissDailySummary}
        style={{
          position: 'fixed', inset: 0, zIndex: 9990,
          backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 9991, width: 520, maxWidth: 'calc(100vw - 32px)',
        backgroundColor: '#0a0d10', borderRadius: 12,
        border: '1px solid #2a3540',
        boxShadow: '0 25px 60px rgba(0,0,0,0.7)',
        maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #10151b',
          position: 'sticky', top: 0, backgroundColor: '#0a0d10', zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BarChart2 size={18} color="#ff8c3a" />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#d7dde4' }}>Day End Report</div>
              <div style={{ fontSize: 11, color: '#7a8693' }}>{d.date} · {dayProgress}</div>
            </div>
          </div>
          <button
            onClick={dismissDailySummary}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#7a8693', padding: 4, borderRadius: 4,
              display: 'flex', alignItems: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* P&L Hero */}
          <div style={{
            backgroundColor: `${pnlColor}10`, border: `1px solid ${pnlColor}30`,
            borderRadius: 8, padding: '14px 18px', marginBottom: 16,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 11, color: '#7a8693', marginBottom: 4 }}>TOTAL P&L (R + U)</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: pnlColor }}>{formatTZS(totalPnL)} TZS</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#7a8693' }}>Cumulative Return</div>
              <div style={{
                fontSize: 18, fontWeight: 700,
                color: d.cumulative_return_pct >= 0 ? '#36c98e' : '#ff5c5c',
              }}>
                {d.cumulative_return_pct >= 0 ? '+' : ''}{d.cumulative_return_pct.toFixed(2)}%
              </div>
            </div>
          </div>

          {/* P&L Breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            <div style={{ backgroundColor: '#10151b', borderRadius: 6, padding: '10px 14px' }}>
              <div style={{ fontSize: 10, color: '#7a8693', marginBottom: 3 }}>REALIZED P&L</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: d.realized_pnl >= 0 ? '#36c98e' : '#ff5c5c' }}>
                {formatTZS(d.realized_pnl)} TZS
              </div>
            </div>
            <div style={{ backgroundColor: '#10151b', borderRadius: 6, padding: '10px 14px' }}>
              <div style={{ fontSize: 10, color: '#7a8693', marginBottom: 3 }}>UNREALIZED P&L</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: d.unrealized_pnl >= 0 ? '#36c98e' : '#ff5c5c' }}>
                {formatTZS(d.unrealized_pnl)} TZS
              </div>
            </div>
          </div>

          {/* Activity stats */}
          <div style={{ backgroundColor: '#10151b', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#7e8a96', fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Activity
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <Stat label="Trades" value={`${d.trade_count}`} />
              <Stat label="Buy / Sell" value={`${d.buy_count} / ${d.sell_count}`} />
              <Stat
                label="Win Rate"
                value={`${d.win_rate_pct.toFixed(0)}%`}
                color={d.win_rate_pct >= 50 ? '#36c98e' : '#ff5c5c'}
              />
              <Stat
                label="Quotes → Fills"
                value={`${d.quote_count} → ${(d.execution_rate * d.quote_count).toFixed(0)}`}
              />
              <Stat
                label="Exec Rate"
                value={`${(d.execution_rate * 100).toFixed(1)}%`}
                color={d.execution_rate >= 0.02 ? '#36c98e' : '#ff8c3a'}
              />
              <Stat
                label="Avg Spread"
                value={`${d.avg_spread_bps.toFixed(0)} bps`}
              />
              <Stat
                label="2-Sided Uptime"
                value={`${d.two_sided_uptime_pct.toFixed(0)}%`}
                color={d.two_sided_uptime_pct >= 70 ? '#36c98e' : '#ff8c3a'}
              />
              <Stat
                label="Day Drawdown"
                value={`${d.day_drawdown_pct.toFixed(1)}%`}
                color={Math.abs(d.day_drawdown_pct) > 2 ? '#ff5c5c' : '#7e8a96'}
              />
            </div>
          </div>

          {/* Top performers podium */}
          {d.top_performers.length > 0 && (
            <div style={{ backgroundColor: '#10151b', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <Award size={13} color="#ff8c3a" />
                <span style={{ fontSize: 11, color: '#7e8a96', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Top Performers
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {d.top_performers.slice(0, 3).map((p, i) => {
                  const medals = ['🥇', '🥈', '🥉'];
                  return (
                    <div key={p.ticker} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 13 }}>{medals[i] || `#${i + 1}`}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#d7dde4' }}>{p.ticker}</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: p.pnl >= 0 ? '#36c98e' : '#ff5c5c' }}>
                        {formatTZS(p.pnl)} TZS
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Worst performer */}
          {d.worst_performer && (
            <div style={{
              backgroundColor: '#1a0a0a', border: '1px solid #ff5c5c30',
              borderRadius: 8, padding: '10px 14px', marginBottom: 16,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingDown size={13} color="#ff5c5c" />
                <span style={{ fontSize: 12, color: '#7e8a96' }}>Laggard: <span style={{ fontWeight: 600, color: '#d7dde4' }}>{d.worst_performer.ticker}</span></span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#ff5c5c' }}>
                {formatTZS(d.worst_performer.pnl)} TZS
              </span>
            </div>
          )}

          {/* Ticker breakdown table */}
          {d.ticker_breakdown.length > 0 && (
            <div style={{ backgroundColor: '#10151b', borderRadius: 8, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: '#7e8a96', fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Ticker Breakdown
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '6px 12px', fontSize: 11 }}>
                <span style={{ color: '#7a8693' }}>Symbol</span>
                <span style={{ color: '#7a8693', textAlign: 'right' }}>Position</span>
                <span style={{ color: '#7a8693', textAlign: 'right' }}>P&L</span>
                <span style={{ color: '#7a8693', textAlign: 'right' }}>Bias</span>
                {d.ticker_breakdown.map(tb => (
                  <React.Fragment key={tb.ticker}>
                    <span style={{ color: '#d7dde4', fontWeight: 600 }}>{tb.ticker}</span>
                    <span style={{ color: '#7e8a96', textAlign: 'right' }}>{tb.position.toLocaleString()}</span>
                    <span style={{
                      textAlign: 'right', fontWeight: 600,
                      color: tb.daily_pnl >= 0 ? '#36c98e' : '#ff5c5c',
                    }}>
                      {formatTZS(tb.daily_pnl)}
                    </span>
                    <span style={{ color: '#7e8a96', textAlign: 'right' }}>{tb.bias_ratio.toFixed(2)}:1</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
